import React, { useState } from "react";
import BusinessDetailsModel from "./formStepComponents/BusinessDetails";
import BussinessAddressModel from "./formStepComponents/BussinessAddress";
import ApplyForWC from "./formStepComponents/ApplyForWC";
import DescribeBusinessModel from "./formStepComponents/DescribeBusiness";
import EPLIModel from "./formStepComponents/EPLI";
import BusinessAttributesModel from "./formStepComponents/BusinessAttributes";
import EmployeeAttributesModel from "./formStepComponents/EmployeeAttributes";
import DocumentsModel from "./formStepComponents/Documents";
import ReviewAndSubmitModel from "./formStepComponents/ReviewAndSubmit";
import styles from "./WorkerCompApplication.module.css";
import Stepper from "uicomponents/Stepper";
import { PageHeader } from "@justworkshr/milo-components";
import {
  PAGE_TITLE0,
  PAGE_TITLE04,
  PAGE_TITLE4,
  PAGE_TITLE_LINK,
  PAGE_TITLE_SUBMITTED,
  STEPS_FOR_OWNER_EMPLOYEE_MERGE_TEST_VARIANT,
} from "./utils/contants";
import { ApplicationType } from "types";
import { useUpdateStepMutation } from "types/generated/operations";
import { errorMessageMap, getCWRedirectUrl } from "utils/helper";
import Error from "uicomponents/Error";
import Alert from "uicomponents/Alert";
import {
  trackFormError,
  trackFormSubmission,
  trackSaveAndContinueUserStep,
  trackStepperError,
} from "utils/analytics/trackEvent";
import { sectionsQuestions } from "./utils/sectionsQuestions";
import OwnerAndEmployeeCensusModel from "./formStepComponents/OwnerAndEmployeeCensus";
import Loader from "uicomponents/Loader";
import Congratulations from "./formStepComponents/Congratulations";

interface IWorkerCompApplicationProps {
  aplicationStatus: ApplicationType;
}

const WorkerCompApplicationTestVariant = ({
  ...props
}: IWorkerCompApplicationProps): JSX.Element => {
  const { aplicationStatus } = props;

  const [errorMessage, setErrorMessage] = useState("");

  const [activeAppStatus, setActiveApp] = React.useState(
    aplicationStatus?.applications?.[0]?.step
  );
  const [activeStepIndex, setActiveStepIndex] = React.useState(
    aplicationStatus?.applications?.[0]?.step || 0
  );

  const [stepError, setStepError] = React.useState(false);

  const [updateStep] = useUpdateStepMutation();

  const nextStep = async () => {
    try {
      if (!aplicationStatus?.applications?.[0]?.uuid) {
        trackStepperError("uuid not found");
        setStepError(true);
        return;
      }
      const updateStepTwice = activeStepIndex === 9 ? true : false;
      if (activeAppStatus === activeStepIndex) {
        await updateStep({
          variables: {
            uuid: aplicationStatus?.applications?.[0].uuid,
            step: activeStepIndex + 1,
          },
        });
        if (updateStepTwice) {
          await updateStep({
            variables: {
              uuid: aplicationStatus?.applications?.[0].uuid,
              step: 11,
            },
          });
        }
        setActiveApp(activeStepIndex + 1);
        setActiveStepIndex(activeStepIndex + 1);
        activeStepIndex === 9
          ? trackFormSubmission(activeStepIndex, "Review")
          : trackSaveAndContinueUserStep(
              activeStepIndex,
              Object.entries(sectionsQuestions)?.[activeStepIndex]?.[0]
            );

        // Redirects to /quoting-checklist dashboard upon submission
        if (updateStepTwice) {
          setTimeout(function () {
            window.location.href = getCWRedirectUrl(
              "/onboarding_checklist?wcappsubmitted=true"
            );
          }, 3000);
        }
      } else {
        setActiveStepIndex(activeAppStatus || 0);
      }
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, `error updating form step- ${error?.message}`);
      setStepError(true);
      return;
    }
  };

  const previousStep = (): void => {
    setActiveStepIndex(activeStepIndex - 1);
  };

  const formList = [
    <ApplyForWC setNextStep={nextStep} />,
    <BusinessDetailsModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <BussinessAddressModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <OwnerAndEmployeeCensusModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <EmployeeAttributesModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <DescribeBusinessModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <BusinessAttributesModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
    />,
    <DocumentsModel setNextStep={nextStep} setPreviousStep={previousStep} />,
    <EPLIModel setNextStep={nextStep} setPreviousStep={previousStep} />,
    <ReviewAndSubmitModel
      setNextStep={nextStep}
      setPreviousStep={previousStep}
      setActiveStepIndex={setActiveStepIndex}
    />,
    <Loader message="Submitting your application" />,
  ];

  const showStepper = activeStepIndex !== 0 && activeStepIndex !== 10;

  const getPageTitle = () => {
    if (activeStepIndex > 9) {
      return PAGE_TITLE_SUBMITTED;
    }
    if (activeStepIndex >= 4) {
      return PAGE_TITLE4;
    }
    if (activeStepIndex > 0 && activeStepIndex <= 3) {
      return PAGE_TITLE04;
    }
    return PAGE_TITLE0;
  };

  if (!aplicationStatus?.applications?.[0]) {
    return <Error />;
  }

  if (aplicationStatus?.applications?.[0].status === "COMPLETED") {
    return <Congratulations />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <PageHeader
          className={styles.pageHeader}
          title={getPageTitle()}
          linkPrevious={
            <a href={getCWRedirectUrl("/onboarding_checklist")}>
              {PAGE_TITLE_LINK}
            </a>
          }
        />
      </div>
      <div className={styles.formContainer}>
        {showStepper && (
          <Stepper
            steps={STEPS_FOR_OWNER_EMPLOYEE_MERGE_TEST_VARIANT}
            currentStep={activeStepIndex}
            status={activeAppStatus || 0}
            onClick={(stepNumber) => setActiveStepIndex(stepNumber)}
          />
        )}
        <div
          className={`${
            showStepper ? styles.formDiv : styles.fullWidth
          } ApplicationStep${activeStepIndex}`}
        >
          {formList[activeStepIndex]}
          {stepError && <Alert message={errorMessage} />}
        </div>
      </div>
    </div>
  );
};

export default WorkerCompApplicationTestVariant;
